import { useMemo } from "react";
import { useSession } from "../auth/session.js";
import { EmptyState, HonestState, ProvenancePopover } from "../components.js";
import {
  liveNarrativeService,
  useCitationLineages,
  type CitationLineageState,
  type NarrativeService,
  type ProjectNarrativeView,
  type WhatChangedView,
} from "../api/narrativeService.js";
import "./project.css";

/**
 * NX-2635 (E8.9) — the Narrative panel + "what changed" digest on Project detail (design/07 §6
 * "Narrative block": left-accent, AI-labeled, cites lineage).
 *
 * WHY THIS IS ITS OWN FILE (AC1), not folded into `ProjectAnalyticsPanels.tsx`: that module's own
 * doc comment states its panels "never fetch — every panel here is presentational... takes its
 * slice of the ALREADY-fetched envelope as props" (E8.7/NX-2632). The narrative comes from a
 * SEPARATE endpoint, `GET /projects/:id/narrative` (E7.6/NX-2639), not `GET /projects/:id`'s
 * envelope — exactly the same reasoning `EstimationPanel.tsx` (NX-2634/E13.8) already used for the
 * same class of decision (a separately-fetched sub-resource on the same screen). This panel is
 * composed into Project.tsx by one JSX line, self-fetching, a sibling of `EstimationPanel`.
 *
 * E7.6 (NX-2639, `GET /projects/:id/narrative`) IS NOT ON `main` YET — it is being built in a
 * PARALLEL story at the time of this one. This panel is built against the REAL artifact shape
 * (`packages/ai/src/narrative.ts`, E6.4/NX-2614, ON `main`) via `narrativeService.ts`'s injected
 * `NarrativeService` (AC1's required seam — "same injected-hook pattern as the rest of the screen").
 * `liveNarrativeService` wires `useApi` to the endpoint E7.6 will register; nothing else in this
 * file talks to `fetch`/`apiGet` directly.
 */

const CITATION_RE = /\[\[fact:(\d+)\]\]/g;

/** Every `[[fact:<id>]]` marker the generated text contains (packages/ai/src/grounding.ts's own
 *  `CITATION_RE` — the exact syntax the model is instructed to emit and the exact syntax
 *  `parseCitations`/`validateCitations` there enforce server-side). Never a second citation-marker
 *  syntax invented on the client. */
function citationIdsIn(text: string | null | undefined): readonly number[] {
  // Defensive beyond the declared `string | null` type: E7.6 (NX-2639) is not on `main` yet, so this
  // guards against a response that does not (yet, or ever) match the mirrored shape exactly — the
  // same "a screen must degrade rather than throw" rule `assessmentService.ts`'s `versionsOf` already
  // follows for its own history envelope.
  if (typeof text !== "string") return [];
  return [...text.matchAll(CITATION_RE)].map((m) => Number(m[1]));
}

/** AC2 — one citing claim's provenance trigger, reusing the EXISTING `ProvenancePopover`
 *  (components.tsx) unmodified. Always renders SOMETHING focusable/clickable regardless of
 *  resolution state (loading/ready/error/unresolved) — TC1 needs both citations to be real
 *  provenance triggers the instant the narrative renders, not only once every lineage lookup has
 *  finished. An unresolved citation is shown honestly (struck-through `[n]`, `source:"unresolved"`)
 *  rather than silently dropped or rendered as inert plain text. */
function CitationTrigger({
  factId,
  state,
}: {
  factId: number;
  state: CitationLineageState | undefined;
}): React.ReactNode {
  if (state === undefined || state.status === "loading") {
    return (
      <ProvenancePopover source="resolving…" method="lineage lookup in progress" confidence={null}>
        <span className="cit">[{factId}]</span>
      </ProvenancePopover>
    );
  }
  if (state.status === "error") {
    return (
      <ProvenancePopover
        source="unresolved"
        method="provenance lookup unavailable"
        confidence={null}
      >
        <span className="cit unresolved">[{factId}]</span>
      </ProvenancePopover>
    );
  }
  return (
    <ProvenancePopover
      source={state.provenance.source}
      method={state.provenance.method}
      confidence={state.provenance.confidence}
      freshness={state.provenance.effectiveAt.slice(0, 10)}
    >
      <span className="cit">[{factId}]</span>
    </ProvenancePopover>
  );
}

/**
 * AC2/TC4 — splits generated prose on `[[fact:<id>]]` markers into plain-text React children and
 * `CitationTrigger` elements. Every text segment is a REACT STRING CHILD, never HTML: React escapes
 * string children by construction, so source-derived text (including a `<script>` an adversarial or
 * merely buggy upstream fact `data` field might contain — the model prompt only ever sees fact
 * content as untrusted DATA, per grounding.ts's own AC4, but this component does not trust that
 * upstream discipline either) can never be interpreted as markup (TC4). This function must never be
 * rewritten to build an HTML string or call `dangerouslySetInnerHTML` — NarrativePanel.test.tsx's
 * mutation drill asserts exactly that swap turns TC4 red.
 */
function renderCitedText(
  text: string,
  lineages: ReadonlyMap<number, CitationLineageState>,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(CITATION_RE)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    const factId = Number(match[1]);
    nodes.push(
      <CitationTrigger key={`cit-${key++}`} factId={factId} state={lineages.get(factId)} />,
    );
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

// ── What-changed digest (AC3) ───────────────────────────────────────────────────────────────────

function WhatChangedDigest({
  whatChanged,
  lineages,
}: {
  // Defensive beyond the declared type, same reasoning as `citationIdsIn` — E7.6 is not on `main` yet.
  whatChanged: WhatChangedView | undefined;
  lineages: ReadonlyMap<number, CitationLineageState>;
}): React.ReactNode {
  if (
    whatChanged === undefined ||
    whatChanged.status !== "generated" ||
    typeof whatChanged.text !== "string"
  ) {
    // AC3/TC3 — the honest state is whatever the server's OWN `reason` says, relayed VERBATIM (the
    // same rule EstimationPanel.tsx follows for `coverage.reason`). This component composes no
    // "no changes"/"no prior visit" sentence of its own — it cannot fabricate one because it never
    // writes one; see NarrativePanel.test.tsx's mutation drill for TC3, which swaps this branch for
    // a hardcoded "No changes since your last visit" and confirms the test goes red. Not wrapped in
    // the `.narr` AI-labeled block: nothing was actually generated here, so labeling it "AI" would
    // itself be a small dishonesty.
    return (
      <HonestState kind="notobs" detail={whatChanged?.reason ?? "What changed: not available."} />
    );
  }
  return (
    <div className="narr">
      <span className="aitag">AI</span>
      <h4>What changed in the last seven days</h4>
      <p>{renderCitedText(whatChanged.text, lineages)}</p>
    </div>
  );
}

// ── Main narrative body ─────────────────────────────────────────────────────────────────────────

function NarrativeBody({
  data,
  lineages,
}: {
  data: ProjectNarrativeView;
  lineages: ReadonlyMap<number, CitationLineageState>;
}): React.ReactNode {
  return (
    <>
      {data.status === "generated" && typeof data.text === "string" ? (
        <div className="narr">
          <span className="aitag">AI</span>
          {renderCitedText(data.text, lineages)}
        </div>
      ) : data.status === "opening" && typeof data.text === "string" ? (
        // NX-2701/AC5 — a real, cited opening statement, but NOT model output: no "AI" tag, unlike
        // the "generated" branch above. Citations still resolve exactly the same way (the panel's
        // lineage lookups run off `[[fact:N]]` markers in `data.text` regardless of status).
        <div className="narr">{renderCitedText(data.text, lineages)}</div>
      ) : (
        // AC4/TC2 — the honest degraded state (design/07 §6 "Honest-degradation state") for a thin
        // basis (E6.2's categorical refusal) or an unconfigured AI provider — never a hedge-free
        // sentence overstating certainty (ADR-016). `reason` is relayed verbatim, same rule as above.
        <HonestState
          kind={data.status === "unavailable" ? "notobs" : "lowc"}
          detail={data.reason ?? "Narrative: not available."}
        />
      )}
      <WhatChangedDigest whatChanged={data.whatChanged} lineages={lineages} />
    </>
  );
}

// ── Top-level, self-fetching panel ──────────────────────────────────────────────────────────────

export function NarrativePanel({
  projectId,
  service = liveNarrativeService,
}: {
  projectId: string;
  service?: NarrativeService;
}): React.ReactNode {
  const { session } = useSession();
  const state = service.useNarrative(projectId);

  const citationIds = useMemo(() => {
    if (state.status !== "ready") return [];
    const d = state.envelope.data;
    return [...citationIdsIn(d.text), ...citationIdsIn(d.whatChanged?.text)];
  }, [state]);

  const lineages = useCitationLineages(projectId, citationIds, session?.token ?? null);

  // NX-2701/AC5 — "opening" is a real, cited statement but NOT model output (see NarrativeBody's
  // "opening" branch, which likewise withholds the "AI" tag). The header must not call it
  // "AI-generated" just because the panel usually shows model output.
  const isAiGenerated = state.status !== "ready" || state.envelope.data.status !== "opening";

  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Narrative</h3>
        <span className="cnt">
          {isAiGenerated
            ? "AI-generated summary · what changed in the last seven days"
            : "What changed in the last seven days"}
        </span>
      </div>
      {state.status === "loading" && <div className="meta">Generating the project narrative…</div>}
      {state.status === "error" &&
        (state.error.status === 403 ? (
          // AC6 — a role the API withheld the narrative from. Rendered as an honest withheld state,
          // never a generic error and never a blank panel (which would read as "nothing here" rather
          // than "withheld"). This component holds no local copy of a previously-successful `data`,
          // so there is no stale narrative it could fall back to rendering instead.
          <HonestState
            kind="notobs"
            detail="The narrative for this project is not available for your role."
          />
        ) : (
          <EmptyState
            message={
              state.error.status === 404
                ? "Narrative is not available for this project."
                : `Couldn't load the narrative: ${state.error.message}`
            }
          />
        ))}
      {state.status === "ready" && <NarrativeBody data={state.envelope.data} lineages={lineages} />}
    </>
  );
}
